import { Plant } from '../plants/Plant';

export class PlantRenderer {
  private ctx: CanvasRenderingContext2D;
  private colorCache = new Map<string, string>();

  constructor(ctx: CanvasRenderingContext2D) {
    this.ctx = ctx;
  }

  render(plant: Plant, x: number, y: number, canvasHeight: number, time: number): void {
    const stage = plant.getCurrentStage();
    if (!stage) return;

    this.renderSoilMound(x, y, canvasHeight);

    this.colorCache.clear();

    const visualState = plant.getVisualState();

    const soilTop = canvasHeight * 0.75;
    const depth = Math.max(0, Math.min(1, (y - soilTop) / (canvasHeight - soilTop)));
    const pScale = 0.92 + depth * 0.08;
    
    this.ctx.save();
    this.ctx.globalAlpha = visualState.alpha;
    
    this.ctx.translate(x, y);
    this.ctx.scale(pScale, pScale);
    this.ctx.rotate(visualState.droopAngle * Math.PI / 180);

    const sway = Math.sin(time * 0.0015 * (1 + visualState.swayAmplitude * 0.5)) * visualState.swayAmplitude * 0.02;
    this.ctx.rotate(sway);

    this.ctx.scale(1, 1 + visualState.swayAmplitude * 0.0003 * Math.sin(time * 0.002 + 1));

    switch (stage.id) {
      case 'seed':
        this.renderSeed(visualState, time, plant.species.id);
        break;
      case 'sprout':
        this.renderSprout(plant.displayProgress, visualState, time, plant.species.id);
        break;
      case 'seedling':
        this.renderSeedling(plant.displayProgress, visualState, time, plant.species.id);
        break;
      case 'budding':
        this.renderBudding(plant.displayProgress, visualState, time, plant.species.id);
        break;
      case 'blooming':
        this.renderBlooming(plant.displayProgress, visualState, time, plant.species.id);
        break;
    }

    this.ctx.restore();
  }

  private renderSeed(visualState: any, _time: number, speciesId: string): void {
    if (speciesId === 'sunflower') {
      this.ctx.fillStyle = this.adjustColor('#2a2018', visualState);
      this.ctx.beginPath();
      this.ctx.ellipse(0, 0, 4, 3, 0, 0, Math.PI * 2);
      this.ctx.fill();
      this.ctx.fillStyle = this.adjustColor('#e8e0d0', visualState);
      this.ctx.beginPath();
      this.ctx.ellipse(0.5, 0, 1.5, 2, 0.2, 0, Math.PI * 2);
      this.ctx.fill();
      return;
    }
    this.ctx.fillStyle = this.adjustColor('#4a3728', visualState);
    this.ctx.beginPath();
    this.ctx.ellipse(0, 0, 3, 2, 0, 0, Math.PI * 2);
    this.ctx.fill();
  }

  private renderSprout(progress: number, visualState: any, _time: number, speciesId: string): void {
    if (speciesId === 'sunflower') {
      const height = 20 * progress;
      const width = 16 * progress;

      this.ctx.fillStyle = this.adjustColor('#7a9a4a', visualState);
      this.ctx.beginPath();
      this.ctx.moveTo(0, 0);
      this.ctx.quadraticCurveTo(-width, -height * 0.6, -width * 0.8, -height);
      this.ctx.quadraticCurveTo(-width * 0.3, -height * 0.5, 0, 0);
      this.ctx.fill();

      this.ctx.beginPath();
      this.ctx.moveTo(0, 0);
      this.ctx.quadraticCurveTo(width, -height * 0.6, width * 0.8, -height);
      this.ctx.quadraticCurveTo(width * 0.3, -height * 0.5, 0, 0);
      this.ctx.fill();

      this.ctx.strokeStyle = this.adjustColor('#5a7a3a', visualState);
      this.ctx.lineWidth = 2.5;
      this.ctx.beginPath();
      this.ctx.moveTo(0, 0);
      this.ctx.lineTo(0, -height * 0.35);
      this.ctx.stroke();
      return;
    }
    const height = 16 * progress;
    const width = 12 * progress;

    this.ctx.fillStyle = this.adjustColor('#8B9A46', visualState);
    
    this.ctx.beginPath();
    this.ctx.moveTo(0, 0);
    this.ctx.quadraticCurveTo(-width/2, -height/2, -width/3, -height);
    this.ctx.quadraticCurveTo(0, -height/2, 0, 0);
    this.ctx.fill();

    this.ctx.beginPath();
    this.ctx.moveTo(0, 0);
    this.ctx.quadraticCurveTo(width/2, -height/2, width/3, -height);
    this.ctx.quadraticCurveTo(0, -height/2, 0, 0);
    this.ctx.fill();

    this.ctx.strokeStyle = this.adjustColor('#6B8E3A', visualState);
    this.ctx.lineWidth = 2;
    this.ctx.beginPath();
    this.ctx.moveTo(0, 0);
    this.ctx.lineTo(0, -height * 0.3);
    this.ctx.stroke();
  }

  private renderSeedling(progress: number, visualState: any, time: number, speciesId: string): void {
    if (speciesId === 'sunflower') {
      const height = 40 + 60 * progress;
      const leafCount = 5;
      
      const stemSway = Math.sin(time * 0.002 + 0.5) * visualState.swayAmplitude * 0.3;
      
      this.ctx.strokeStyle = this.adjustColor('#5a8a3a', visualState);
      this.ctx.lineWidth = 4;
      this.ctx.beginPath();
      this.ctx.moveTo(0, 0);
      this.ctx.quadraticCurveTo(3 + stemSway, -height/2, stemSway, -height);
      this.ctx.stroke();

      this.ctx.fillStyle = this.adjustColor('#6aaa4a', visualState);
      for (let i = 0; i < leafCount * progress; i++) {
        const y = -height * (0.25 + (i / leafCount) * 0.55);
        const side = i % 2 === 0 ? -1 : 1;
        const leafLen = 22 + 8 * Math.sin(i * 1.2);
        const leafSway = stemSway * (1 - i / leafCount) * 0.5;

        this.ctx.save();
        this.ctx.translate(leafSway, y);
        this.ctx.rotate(side * (Math.PI * 0.3));
        
        this.ctx.beginPath();
        this.ctx.moveTo(0, 0);
        this.ctx.quadraticCurveTo(leafLen * 0.3, -leafLen * 0.15, leafLen, -leafLen * 0.05);
        this.ctx.quadraticCurveTo(leafLen * 0.4, leafLen * 0.12, 0, 0);
        this.ctx.fill();
        
        this.ctx.restore();
      }
      return;
    }
    const height = 30 + 40 * progress;
    const leafCount = 6;
    
    const stemSway = Math.sin(time * 0.002 + 0.5) * visualState.swayAmplitude * 0.3;
    
    this.ctx.strokeStyle = this.adjustColor('#6a7a5a', visualState);
    this.ctx.lineWidth = 3;
    this.ctx.beginPath();
    this.ctx.moveTo(0, 0);
    this.ctx.quadraticCurveTo(2 + stemSway, -height/2, stemSway, -height);
    this.ctx.stroke();

    this.ctx.fillStyle = this.adjustColor('#7a8a6a', visualState);
    for (let i = 0; i < leafCount * progress; i++) {
      const y = -height * (0.3 + (i / leafCount) * 0.6);
      const side = i % 2 === 0 ? -1 : 1;
      const leafLen = 15 + 5 * Math.sin(i);
      const leafSway = stemSway * (1 - i / leafCount) * 0.5;

      this.ctx.save();
      this.ctx.translate(leafSway, y);
      this.ctx.rotate(side * (Math.PI * 0.35));
      
      this.ctx.beginPath();
      this.ctx.moveTo(0, 0);
      this.ctx.quadraticCurveTo(leafLen * 0.3, -leafLen * 0.08, leafLen, 0);
      this.ctx.quadraticCurveTo(leafLen * 0.3, leafLen * 0.08, 0, 0);
      this.ctx.fill();
      
      this.ctx.restore();
    }
  }

  private renderBudding(progress: number, visualState: any, time: number, speciesId: string): void {
    if (speciesId === 'sunflower') {
      const height = 80 + 70 * progress;
      
      const stemSway = Math.sin(time * 0.002 + 1) * visualState.swayAmplitude * 0.2;
      
      this.ctx.strokeStyle = this.adjustColor('#5a8a3a', visualState);
      this.ctx.lineWidth = 4;
      this.ctx.beginPath();
      this.ctx.moveTo(0, 0);
      this.ctx.quadraticCurveTo(2 + stemSway, -height * 0.5, stemSway, -height);
      this.ctx.stroke();

      const leafCount = 8;
      for (let i = 0; i < leafCount; i++) {
        const t = 0.1 + (i / leafCount) * 0.5;
        const ly = -height * t;
        const side = i % 2 === 0 ? -1 : 1;
        const leafLen = 18 + 6 * Math.sin(i * 1.3);
        const leafAngle = side * (Math.PI * 0.3);
        const leafSway = stemSway * t * 2;
        
        this.ctx.strokeStyle = this.adjustColor('#5a8a3a', visualState);
        this.ctx.lineWidth = 1.5;
        this.ctx.beginPath();
        this.ctx.moveTo(leafSway, ly);
        this.ctx.lineTo(side * 7 + leafSway, ly - 3);
        this.ctx.stroke();
        
        this.ctx.fillStyle = this.adjustColor('#6aaa4a', visualState);
        this.ctx.save();
        this.ctx.translate(side * 7 + leafSway, ly - 3);
        this.ctx.rotate(leafAngle);
        
        this.ctx.beginPath();
        this.ctx.moveTo(0, 0);
        this.ctx.quadraticCurveTo(leafLen * 0.35, -leafLen * 0.12, leafLen, -leafLen * 0.05);
        this.ctx.quadraticCurveTo(leafLen * 0.4, leafLen * 0.1, 0, 0);
        this.ctx.fill();
        
        this.ctx.restore();
      }

      const budY = -height;
      const budSize = 8 + 12 * progress;
      
      this.ctx.fillStyle = this.adjustColor('#4a7a3a', visualState);
      this.ctx.beginPath();
      this.ctx.arc(stemSway, budY, budSize * 0.6, 0, Math.PI * 2);
      this.ctx.fill();

      for (let i = 0; i < 5; i++) {
        const angle = (Math.PI * 2 * i / 5) + Math.PI * 0.15;
        const sepX = stemSway + Math.cos(angle) * budSize * 0.35;
        const sepY = budY + Math.sin(angle) * budSize * 0.35;
        this.ctx.fillStyle = this.adjustColor('#3a6a2a', visualState);
        this.ctx.beginPath();
        this.ctx.ellipse(sepX, sepY, 3, budSize * 0.45, angle, 0, Math.PI * 2);
        this.ctx.fill();
      }

      if (progress > 0.4) {
        const yellowReveal = (progress - 0.4) / 0.6;
        this.ctx.fillStyle = this.adjustColor(`rgba(230, 200, 60, ${yellowReveal * 0.6})`, visualState);
        this.ctx.beginPath();
        this.ctx.arc(stemSway, budY, budSize * 0.35 * yellowReveal, 0, Math.PI * 2);
        this.ctx.fill();
      }
      return;
    }
    const height = 70 + 50 * progress;
    
    const stemSway = Math.sin(time * 0.002 + 1) * visualState.swayAmplitude * 0.2;
    
    this.ctx.strokeStyle = this.adjustColor('#6a7a5a', visualState);
    this.ctx.lineWidth = 3;
    this.ctx.beginPath();
    this.ctx.moveTo(0, 0);
    this.ctx.quadraticCurveTo(1 + stemSway, -height * 0.5, stemSway, -height);
    this.ctx.stroke();

    const leafCount = 10;
    for (let i = 0; i < leafCount; i++) {
      const t = 0.15 + (i / leafCount) * 0.5;
      const ly = -height * t;
      const side = i % 2 === 0 ? -1 : 1;
      const leafLen = 12 + 4 * Math.sin(i * 1.5);
      const leafAngle = side * (Math.PI * 0.35);
      const leafSway = stemSway * t * 2;
      
      this.ctx.strokeStyle = this.adjustColor('#6a7a5a', visualState);
      this.ctx.lineWidth = 1.5;
      this.ctx.beginPath();
      this.ctx.moveTo(leafSway, ly);
      this.ctx.lineTo(side * 6 + leafSway, ly - 3);
      this.ctx.stroke();
      
      this.ctx.fillStyle = this.adjustColor('#7a9a5a', visualState);
      this.ctx.save();
      this.ctx.translate(side * 6 + leafSway, ly - 3);
      this.ctx.rotate(leafAngle);
      
      this.ctx.beginPath();
      this.ctx.moveTo(0, 0);
      this.ctx.quadraticCurveTo(leafLen * 0.4, -leafLen * 0.1, leafLen, 0);
      this.ctx.quadraticCurveTo(leafLen * 0.4, leafLen * 0.1, 0, 0);
      this.ctx.fill();
      
      this.ctx.restore();
    }

    const spikeHeight = 15 + 20 * progress;
    const spikeStartY = -height;
    const budDensity = 0.3 + progress * 0.7;
    const spikeSway = stemSway * 1.5;
    
    for (let i = 0; i < 12; i++) {
      const t = i / 12;
      const by = spikeStartY + spikeHeight * t;
      const layerWidth = 4 + 8 * Math.sin(t * Math.PI) * budDensity;
      
      for (let s = -1; s <= 1; s += 2) {
        const bx = s * layerWidth * 0.5;
        const budSize = 3 + 3 * budDensity;
        
        this.ctx.fillStyle = this.adjustColor('#6a7a5a', visualState);
        this.ctx.beginPath();
        this.ctx.ellipse(bx + spikeSway, by, 3, 4, s * 0.2, 0, Math.PI * 2);
        this.ctx.fill();
        
        if (budDensity > 0.3) {
          this.ctx.fillStyle = this.adjustColor('#8a6aa0', visualState);
          this.ctx.beginPath();
          this.ctx.arc(bx + spikeSway, by - 2, budSize * 0.4, 0, Math.PI * 2);
          this.ctx.fill();
        }
      }
      
      if (i < 3) {
        this.ctx.fillStyle = this.adjustColor('#7a6a9a', visualState);
        this.ctx.beginPath();
        this.ctx.ellipse(spikeSway, spikeStartY + i * 2, 3, 4, 0, 0, Math.PI * 2);
        this.ctx.fill();
      }
    }
  }

  private renderBlooming(_progress: number, visualState: any, time: number, speciesId: string): void {
    if (speciesId === 'sunflower') {
      const height = 150;
      
      const stemSway = Math.sin(time * 0.0018 + 1.5) * visualState.swayAmplitude * 0.15;
      
      this.ctx.strokeStyle = this.adjustColor('#5a8a3a', visualState);
      this.ctx.lineWidth = 4;
      this.ctx.beginPath();
      this.ctx.moveTo(0, 0);
      this.ctx.quadraticCurveTo(2 + stemSway, -height * 0.5, stemSway, -height);
      this.ctx.stroke();

      const leafCount = 10;
      for (let i = 0; i < leafCount; i++) {
        const t = 0.1 + (i / leafCount) * 0.5;
        const ly = -height * t;
        const side = i % 2 === 0 ? -1 : 1;
        const leafLen = 20 + 8 * Math.sin(i * 1.5);
        const leafAngle = side * (Math.PI * 0.3);
        const leafSway = stemSway * t * 2;
        
        this.ctx.strokeStyle = this.adjustColor('#5a8a3a', visualState);
        this.ctx.lineWidth = 1.5;
        this.ctx.beginPath();
        this.ctx.moveTo(leafSway, ly);
        this.ctx.lineTo(side * 7 + leafSway, ly - 3);
        this.ctx.stroke();
        
        this.ctx.fillStyle = this.adjustColor('#6aaa4a', visualState);
        this.ctx.save();
        this.ctx.translate(side * 7 + leafSway, ly - 3);
        this.ctx.rotate(leafAngle);
        
        this.ctx.beginPath();
        this.ctx.moveTo(0, 0);
        this.ctx.quadraticCurveTo(leafLen * 0.35, -leafLen * 0.12, leafLen, -leafLen * 0.05);
        this.ctx.quadraticCurveTo(leafLen * 0.4, leafLen * 0.1, 0, 0);
        this.ctx.fill();
        
        this.ctx.restore();
      }

      const headY = -height;
      const headRadius = 22;
      
      const petalCount = 16;
      for (let i = 0; i < petalCount; i++) {
        const angle = (Math.PI * 2 * i / petalCount) + time * 0.0001;
        const petalLen = 14 + 3 * Math.sin(i * 2 + time * 0.001);
        const petalWidth = 4 + 1.5 * Math.sin(i * 1.3);
        
        const px = stemSway + Math.cos(angle) * (headRadius * 0.6);
        const py = headY + Math.sin(angle) * (headRadius * 0.6);
        
        this.ctx.fillStyle = this.adjustColor('#e8c840', visualState);
        this.ctx.save();
        this.ctx.translate(px, py);
        this.ctx.rotate(angle);
        this.ctx.beginPath();
        this.ctx.moveTo(0, 0);
        this.ctx.quadraticCurveTo(petalLen * 0.5, -petalWidth * 0.5, petalLen, 0);
        this.ctx.quadraticCurveTo(petalLen * 0.5, petalWidth * 0.5, 0, 0);
        this.ctx.fill();
        this.ctx.restore();
      }

      this.ctx.fillStyle = this.adjustColor('#5a3a1a', visualState);
      this.ctx.beginPath();
      this.ctx.arc(stemSway, headY, headRadius * 0.65, 0, Math.PI * 2);
      this.ctx.fill();

      const seedCount = 20;
      for (let i = 0; i < seedCount; i++) {
        const angle = (Math.PI * 2 * i / seedCount) * 3.5;
        const r = (i / seedCount) * headRadius * 0.55;
        const sx = stemSway + Math.cos(angle) * r;
        const sy = headY + Math.sin(angle) * r;
        this.ctx.fillStyle = this.adjustColor('#3a2a10', visualState);
        this.ctx.beginPath();
        this.ctx.arc(sx, sy, 1, 0, Math.PI * 2);
        this.ctx.fill();
      }

      const warmGlow = 0.08 + 0.04 * Math.sin(time * 0.002);
      this.ctx.globalAlpha = warmGlow * visualState.alpha;
      const glow = this.ctx.createRadialGradient(stemSway, headY, headRadius * 0.3, stemSway, headY, headRadius * 1.5);
      glow.addColorStop(0, 'rgba(255, 220, 80, 0.5)');
      glow.addColorStop(1, 'rgba(255, 220, 80, 0)');
      this.ctx.fillStyle = glow;
      this.ctx.beginPath();
      this.ctx.arc(stemSway, headY, headRadius * 1.5, 0, Math.PI * 2);
      this.ctx.fill();
      this.ctx.globalAlpha = visualState.alpha;
      return;
    }
    const height = 130;
    
    const stemSway = Math.sin(time * 0.0018 + 1.5) * visualState.swayAmplitude * 0.15;
    
    this.ctx.strokeStyle = this.adjustColor('#6a7a5a', visualState);
    this.ctx.lineWidth = 3;
    this.ctx.beginPath();
    this.ctx.moveTo(0, 0);
    this.ctx.quadraticCurveTo(1 + stemSway, -height * 0.5, stemSway, -height);
    this.ctx.stroke();

    const leafCount = 12;
    for (let i = 0; i < leafCount; i++) {
      const t = 0.1 + (i / leafCount) * 0.5;
      const ly = -height * t;
      const side = i % 2 === 0 ? -1 : 1;
      const leafLen = 14 + 6 * Math.sin(i * 1.8);
      const leafAngle = side * (Math.PI * 0.35);
      const leafSway = stemSway * t * 2;
      
      this.ctx.strokeStyle = this.adjustColor('#6a7a5a', visualState);
      this.ctx.lineWidth = 1.5;
      this.ctx.beginPath();
      this.ctx.moveTo(leafSway, ly);
      this.ctx.lineTo(side * 5 + leafSway, ly - 3);
      this.ctx.stroke();
      
      this.ctx.fillStyle = this.adjustColor('#7a9a5a', visualState);
      this.ctx.save();
      this.ctx.translate(side * 5 + leafSway, ly - 3);
      this.ctx.rotate(leafAngle);
      
      this.ctx.beginPath();
      this.ctx.moveTo(0, 0);
      this.ctx.quadraticCurveTo(leafLen * 0.3, -leafLen * 0.08, leafLen, 0);
      this.ctx.quadraticCurveTo(leafLen * 0.3, leafLen * 0.08, 0, 0);
      this.ctx.fill();
      
      this.ctx.restore();
    }

    const spikeHeight = 55;
    const spikeStartY = -height;
    const whorlCount = 14;
    
    for (let w = 0; w < whorlCount; w++) {
      const t = w / whorlCount;
      const wy = spikeStartY + spikeHeight * t;
      const spikeWidth = 3 + 10 * Math.sin(t * Math.PI);
      const purpleHue = 0.75 + 0.15 * Math.sin(t * Math.PI);
      const flowersPerWhorl = 2 + Math.floor(2 * Math.sin(t * Math.PI));
      
      for (let f = 0; f < flowersPerWhorl; f++) {
        const fx = -spikeWidth * 0.5 + (f / (flowersPerWhorl - 1)) * spikeWidth;
        
        const calyxSize = 3 + 2 * Math.sin(t * Math.PI);
        this.ctx.fillStyle = this.adjustColor('#5a6a4a', visualState);
        this.ctx.beginPath();
        this.ctx.ellipse(fx + stemSway, wy, calyxSize * 0.6, calyxSize, 0, 0, Math.PI * 2);
        this.ctx.fill();
        
        const petalCount = 4;
        const petalSize = 2.5 + 2 * Math.sin(t * Math.PI + time * 0.002 + w + f * 0.5);
        const baseAngle = (Math.PI * f / flowersPerWhorl) + time * 0.0003;
        
        for (let p = 0; p < petalCount; p++) {
          const angle = baseAngle + (Math.PI * 2 * p / petalCount);
          const px = fx + stemSway + Math.cos(angle) * petalSize * 0.5;
          const py = wy - petalSize * 0.8;

          const topHeavy = Math.sin(t * Math.PI);
          const purpleLight = 160 + 60 * topHeavy + 30 * Math.sin(p * 0.8 + t * 2);
          const r = Math.round(130 * purpleHue + 20 * topHeavy);
          const g = Math.round(100 * purpleHue + 10 * topHeavy);
          const petalColor = this.adjustColor(
            `rgb(${r}, ${g}, ${purpleLight})`,
            visualState
          );
          this.ctx.fillStyle = petalColor;
          this.ctx.beginPath();
          this.ctx.ellipse(px, py, petalSize * 0.6, petalSize * 0.9, angle * 0.3, 0, Math.PI * 2);
          this.ctx.fill();
        }

        this.ctx.fillStyle = this.adjustColor(`rgba(255, 230, 180, 0.9)`, visualState);
        this.ctx.beginPath();
        this.ctx.arc(fx + stemSway, wy - 1.5, petalSize * 0.25, 0, Math.PI * 2);
        this.ctx.fill();
      }
    }
    
    const topFlowers = 3;
    for (let i = 0; i < topFlowers; i++) {
      const ty = spikeStartY - 3 - i * 3;
      const size = 2 + i * 0.5;
      
      this.ctx.fillStyle = this.adjustColor('#8a6aa0', visualState);
      this.ctx.beginPath();
      this.ctx.ellipse(stemSway, ty, size * 0.5, size, 0, 0, Math.PI * 2);
      this.ctx.fill();
    }
    
    const glowAlpha = (0.12 + 0.06 * Math.sin(time * 0.002)) * visualState.alpha;
    this.ctx.globalAlpha = glowAlpha;
    for (let w = 2; w < whorlCount - 1; w++) {
      const t = w / whorlCount;
      const wy = spikeStartY + spikeHeight * t;
      const glowSize = 18 * Math.sin(t * Math.PI);
      const outerGlow = this.ctx.createRadialGradient(
        stemSway, wy, glowSize * 0.3,
        stemSway, wy, glowSize
      );
      outerGlow.addColorStop(0, `rgba(200, 160, 232, ${0.6})`);
      outerGlow.addColorStop(1, `rgba(200, 160, 232, 0)`);
      this.ctx.fillStyle = outerGlow;
      this.ctx.beginPath();
      this.ctx.arc(stemSway, wy, glowSize, 0, Math.PI * 2);
      this.ctx.fill();
    }

    this.ctx.globalAlpha = glowAlpha * 0.5;
    this.ctx.fillStyle = '#e0c0f8';
    for (let w = 3; w < whorlCount - 2; w++) {
      const t = w / whorlCount;
      const wy = spikeStartY + spikeHeight * t;
      const glowSize = 8 * Math.sin(t * Math.PI);
      this.ctx.beginPath();
      this.ctx.arc(stemSway, wy, glowSize, 0, Math.PI * 2);
      this.ctx.fill();
    }
    this.ctx.globalAlpha = visualState.alpha;
  }

  renderGridHints(
    slots: Array<{ x: number; y: number }>,
    canvasWidth: number,
    canvasHeight: number,
    hoverSlot: { x: number; y: number } | null
  ): void {
    const soilTop = canvasHeight * 0.75;
    for (const slot of slots) {
      const sx = slot.x * canvasWidth;
      const sy = slot.y * canvasHeight;
      if (sy < soilTop - 5) continue;

      const isHovered = hoverSlot !== null &&
        Math.abs(slot.x - hoverSlot.x) < 0.001 &&
        Math.abs(slot.y - hoverSlot.y) < 0.001;

      this.ctx.save();
      this.ctx.translate(sx, sy);

      if (isHovered) {
        this.ctx.fillStyle = 'rgba(180, 160, 130, 0.35)';
        this.ctx.beginPath();
        this.ctx.arc(0, 0, 14, 0, Math.PI * 2);
        this.ctx.fill();

        this.ctx.fillStyle = 'rgba(130, 110, 80, 0.65)';
        this.ctx.beginPath();
        this.ctx.arc(0, 0, 6, 0, Math.PI * 2);
        this.ctx.fill();
      } else {
        this.ctx.fillStyle = 'rgba(130, 110, 90, 0.22)';
        this.ctx.beginPath();
        this.ctx.arc(0, 0, 4, 0, Math.PI * 2);
        this.ctx.fill();
      }

      this.ctx.restore();
    }
  }

  private renderSoilMound(x: number, y: number, canvasHeight: number): void {
    const soilTop = canvasHeight * 0.75;
    if (y < soilTop - 5) return;

    this.ctx.save();
    this.ctx.translate(x, y);

    // 侧面厚度
    this.ctx.fillStyle = '#3a2a1a';
    this.ctx.beginPath();
    this.ctx.ellipse(0, 4, 27, 10, 0, 0, Math.PI * 2);
    this.ctx.fill();

    // 顶面
    this.ctx.fillStyle = '#5c4030';
    this.ctx.beginPath();
    this.ctx.ellipse(0, 1, 27, 10, 0, 0, Math.PI * 2);
    this.ctx.fill();

    // 高光
    this.ctx.fillStyle = '#7a5a40';
    this.ctx.globalAlpha = 0.35;
    this.ctx.beginPath();
    this.ctx.ellipse(-5, -2, 15, 5, 0, 0, Math.PI * 2);
    this.ctx.fill();

    this.ctx.globalAlpha = 1.0;
    this.ctx.restore();
  }

  hitTest(plant: Plant, plantX: number, plantY: number, clickX: number, clickY: number, margin: number = 0): boolean {
    const stage = plant.getCurrentStage();
    if (!stage) return false;

    const isSunflower = plant.species.id === 'sunflower';
    let height: number;
    let width: number;

    if (isSunflower) {
      switch (stage.id) {
        case 'seed':
          height = 10;
          width = 14;
          break;
        case 'sprout':
          height = 30 * plant.displayProgress;
          width = 30 * plant.displayProgress;
          break;
        case 'seedling':
          height = 40 + 60 * plant.displayProgress;
          width = 60;
          break;
        case 'budding':
          height = 80 + 70 * plant.displayProgress;
          width = 70;
          break;
        case 'blooming':
          height = 175;
          width = 80;
          break;
        default:
          return false;
      }
    } else {
      switch (stage.id) {
        case 'seed':
          height = 8;
          width = 12;
          break;
        case 'sprout':
          height = 24 * plant.displayProgress;
          width = 24 * plant.displayProgress;
          break;
        case 'seedling':
          height = 30 + 40 * plant.displayProgress;
          width = 50;
          break;
        case 'budding':
          height = 70 + 50 * plant.displayProgress;
          width = 60;
          break;
        case 'blooming':
          height = 135;
          width = 70;
          break;
        default:
          return false;
      }
    }

    const left = plantX - width / 2 - margin;
    const top = plantY - height - margin;
    const right = plantX + width / 2 + margin;
    const bottom = plantY + 10 + margin;

    return clickX >= left && clickX <= right && clickY >= top && clickY <= bottom;
  }

  private adjustColor(baseColor: string, visualState: any): string {
    const cacheKey = `${baseColor}|${visualState.colorSaturation.toFixed(3)}|${visualState.browning.toFixed(3)}`;
    const cached = this.colorCache.get(cacheKey);
    if (cached) return cached;

    let r: number, g: number, b: number;
    if (baseColor.startsWith('#')) {
      r = parseInt(baseColor.slice(1, 3), 16);
      g = parseInt(baseColor.slice(3, 5), 16);
      b = parseInt(baseColor.slice(5, 7), 16);
    } else {
      const match = baseColor.match(/(\d+)/g);
      if (!match || match.length < 3) return baseColor;
      r = parseInt(match[0]);
      g = parseInt(match[1]);
      b = parseInt(match[2]);
    }

    const gray = r * 0.3 + g * 0.59 + b * 0.11;
    const sat = visualState.colorSaturation;
    const newR = Math.round(r * sat + gray * (1 - sat));
    const newG = Math.round(g * sat + gray * (1 - sat));
    const newB = Math.round(b * sat + gray * (1 - sat));

    const brownR = 139, brownG = 119, brownB = 101;
    const brown = visualState.browning;
    const finalR = Math.round(newR * (1 - brown) + brownR * brown);
    const finalG = Math.round(newG * (1 - brown) + brownG * brown);
    const finalB = Math.round(newB * (1 - brown) + brownB * brown);

    const result = `rgb(${finalR}, ${finalG}, ${finalB})`;
    this.colorCache.set(cacheKey, result);
    return result;
  }
}
